import { useEffect, useState } from 'react';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { BeforeInstallPromptEvent, IWindow } from '@interfaces/installPrompt';
import MainLayout from '@components/MainLayout';
import { SessionStorageKey, BooleanString } from '@constants/storage-keys.constants';
import { isIOS, isMacOs } from 'react-device-detect';
import { checkStandaloneMode } from '@helpers/notification.helper';
import { getBrowserAgent } from '@helpers/browser.helpers';
import '@/serviceWorkerListener';
import AddToHomeScreen from './pages/AddToHomeScreen';

declare let window: IWindow;
declare global {
	interface WindowEventMap {
		beforeinstallprompt: BeforeInstallPromptEvent;
	}
}

export default function App({ msalInstance }: { msalInstance: IPublicClientApplication }) {
	const [isStandaloneMode, setIsStandaloneMode] = useState(false);
	const [isInstallAvailable, setIsInstallAvailable] = useState(false);
	const [isInstallPromptHidden, setInstallPromptHidden] = useState(
		JSON.parse(window.sessionStorage.getItem(SessionStorageKey.HIDE_INSTALL) || BooleanString.FALSE),
	);
	const [isIntermediaryScreenHidden, setIntermediaryScreenHidden] = useState(
		JSON.parse(window.sessionStorage.getItem(SessionStorageKey.HIDE_INTERMEDIARY_SCREEN) || BooleanString.FALSE),
	);
	const isDesktopDevice = getBrowserAgent().deviceType === 'Desktop';

	const hideInstallPrompt = () => {
		if (!isInstallPromptHidden) {
			setInstallPromptHidden(true);
			return;
		}
	};

	const dismissInstallPrompt = () => {
		if (!isInstallPromptHidden) {
			setInstallPromptHidden(true);
			window.sessionStorage.setItem(SessionStorageKey.HIDE_INSTALL, BooleanString.TRUE);
			return;
		}
	};

	const promptInstall = async () => {
		if (window.deferredPromptEvent) {
			try {
				window.deferredPromptEvent.prompt();
				await window.deferredPromptEvent.userChoice;
				hideInstallPrompt();
			} catch (error) {
				console.error(error);
			} finally {
				window.deferredPromptEvent = null;
			}
		}
	};

	const checkPWAInstalled = () => {
		const standalone = checkStandaloneMode();
		setIsStandaloneMode(standalone);

		if (isDesktopDevice || standalone) {
			setIntermediaryScreenHidden(true);
			window.sessionStorage.setItem(SessionStorageKey.HIDE_INTERMEDIARY_SCREEN, BooleanString.TRUE);
		}

		if ((isIOS || isMacOs) && !standalone) {
			setIsInstallAvailable(true);
		}
	};

	const promptHandler = (e: BeforeInstallPromptEvent) => {
		window.deferredPromptEvent = e;
		e.preventDefault();
		if (isStandaloneMode) {
			setIsInstallAvailable(false);
		} else {
			setIsInstallAvailable(true);
		}
	};

	const appInstalledHandler = () => {
		window.deferredPromptEvent = null;
		setIsInstallAvailable(false);
	};

	const handleHideIntermediaryScreen = () => {
		window.sessionStorage.setItem(SessionStorageKey.HIDE_INTERMEDIARY_SCREEN, BooleanString.TRUE);
		setIntermediaryScreenHidden(true);
	};

	useEffect(() => {
		window.deferredPromptEvent = null;
		checkPWAInstalled();
		window.addEventListener('beforeinstallprompt', promptHandler);
		window.addEventListener('appinstalled', appInstalledHandler);

		return () => {
			window.removeEventListener('beforeinstallprompt', promptHandler);
			window.removeEventListener('appinstalled', appInstalledHandler);
		};
	}, []);

	if (!isIntermediaryScreenHidden && !isStandaloneMode && !isDesktopDevice) {
		return <AddToHomeScreen onInstall={promptInstall} onProceed={handleHideIntermediaryScreen} />;
	}

	return (
		<MsalProvider instance={msalInstance}>
			<MsalAuthenticationTemplate interactionType={InteractionType.Redirect} errorComponent={() => <></>}>
				<MainLayout
					isInstallPromptHidden={isInstallPromptHidden}
					isInstallAvailable={isInstallAvailable}
					hideInstallPrompt={hideInstallPrompt}
					dismissInstallPrompt={dismissInstallPrompt}
					install={promptInstall}
					isStandaloneMode={isStandaloneMode}></MainLayout>
			</MsalAuthenticationTemplate>
		</MsalProvider>
	);
}
